import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/**
 * Account-nicknames store — per-browser override map of
 * `accountId → display name`.
 *
 * Why this exists
 * ---------------
 * The WHB backend's account record has a `name` field, but right now
 * the PWA can't edit it server-side. Users still want to give their
 * accounts memorable labels ("Sergey Live" instead of the default
 * "Default Live") that follow the account across every surface — the
 * header, the selector dropdown, the Bot drawer's account row, the
 * Orders filter, etc.
 *
 * This store keeps those renames client-side in localStorage. The
 * `useRealAccountsAdapted` hook applies the override at the
 * mapping boundary, so all downstream code (header, selectors,
 * search, drawers) transparently sees the renamed value without each
 * site needing to know about the store. Settings is the one place
 * that has to know explicitly — it both reads (to show the current
 * effective name) and writes (when the user edits).
 *
 * Contract
 * --------
 * • `overrides`     — keyed by `account.id`; absent or empty string
 *                     means "no override" (fall back to API value).
 * • `getNickname`   — convenience read: returns the override if any,
 *                     else the fallback (typically `account.name`).
 * • `setNickname`   — write; empty/whitespace-only string clears.
 * • `clear/reset`   — remove a single id / wipe everything.
 *
 * Lifetime
 * --------
 * Persists to localStorage under `whb-account-nicknames`. Survives
 * refresh and re-login on the same browser; clearing browser data
 * resets to the API names. Intentionally not wiped on sign-out — the
 * same human signing back in on the same device probably wants their
 * labels back.
 */
interface AccountNicknamesState {
  overrides: Record<string, string>;
  getNickname: (accountId: string, fallback: string) => string;
  setNickname: (accountId: string, name: string) => void;
  clear: (accountId: string) => void;
  reset: () => void;
}

export const useAccountNicknamesStore = create<AccountNicknamesState>()(
  persist(
    (set, get) => ({
      overrides: {},
      getNickname: (accountId, fallback) => {
        const v = get().overrides[accountId];
        return v && v.trim() ? v : fallback;
      },
      setNickname: (accountId, name) =>
        set((s) => {
          const trimmed = name.trim();
          const next = { ...s.overrides };
          if (trimmed) {
            next[accountId] = trimmed;
          } else {
            delete next[accountId];
          }
          return { overrides: next };
        }),
      clear: (accountId) =>
        set((s) => {
          if (!(accountId in s.overrides)) return s;
          const next = { ...s.overrides };
          delete next[accountId];
          return { overrides: next };
        }),
      reset: () => set({ overrides: {} }),
    }),
    {
      name: "whb-account-nicknames",
      storage: createJSONStorage(() => localStorage),
    }
  )
);

/**
 * Pure helper — apply the override map to a list of accounts,
 * returning a new array with `name` swapped where an override exists.
 * Used inside the data hooks so every consumer of `BrokerAccount[]`
 * sees the renamed value without per-site changes.
 */
export function applyAccountNicknames<T extends { id: string; name: string }>(
  accounts: T[],
  overrides: Record<string, string>
): T[] {
  if (!Object.keys(overrides).length) return accounts;
  return accounts.map((a) => {
    const v = overrides[a.id];
    return v && v.trim() ? { ...a, name: v.trim() } : a;
  });
}
